import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useWallet, validateWalletAddress } from '@/lib/onChain';

import { BaseLayout } from '../../components/Layout';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useBackendAPI } from '../../data-hooks/api-provider';
import { CHAIN_TYPE } from '../../lib/constants/auth';
import { trm } from '../../lib/kyc';

const enum InviteWalletRequestError {
  INVALID_INVITE_ID = 'Ensure that the invite id is valid.',
  INVALID_ADDRESS = 'Please enter a valid wallet address.',
  WALLET_SCREEN_FAILURE_ERROR = 'This address has been flagged by TRM as a high risk address. Please contact us at support@magna.so if you believe this is an error.',
}

export default function InviteWalletRequestPage() {
  const router = useRouter();
  const { publicKey, connected } = useWallet();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [walletAddress, setWalletAddress] = useState<string>(publicKey ?? '');
  const [error, setError] = useState<InviteWalletRequestError>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { api } = useBackendAPI();

  useEffect(() => {
    if (connected && publicKey) setWalletAddress(publicKey);
  }, [connected, publicKey]);

  const acceptInvite = async () => {
    const isValidAddress = await validateWalletAddress(process.env.NEXT_PUBLIC_CHAIN, walletAddress);

    const passesWalletScreen = await trm(walletAddress!);
    if (!passesWalletScreen) {
      setError(InviteWalletRequestError.WALLET_SCREEN_FAILURE_ERROR);
      return;
    }

    if (!isValidAddress) {
      setError(InviteWalletRequestError.INVALID_ADDRESS);
      return;
    } else {
      setError(undefined);
    }

    setIsLoading(true);
    const inviteUpdated = await api.inviteAPI.updateInvite({
      inviteId: router.query._id as string,
      address: walletAddress,
    });

    if (!inviteUpdated) {
      setError(InviteWalletRequestError.INVALID_INVITE_ID);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSubmitted(true);
  };

  const isInvalidInvite = error === InviteWalletRequestError.INVALID_INVITE_ID;

  return (
    <BaseLayout chain={process.env.NEXT_PUBLIC_CHAIN}>
      <Head>
        <title>Magna Invitation</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>

      <div className="mt-8 text-center">
        {!submitted ? (
          <>
            <h1 className="text-4xl font-bold text-zinc-300">
              Welcome to{' '}
              <span className="bg-gradient-to-br from-purple-800 to-red-500 bg-clip-text text-transparent">Magna.</span>
            </h1>
            <p className="text-regular mb-8 mt-2 text-zinc-400">
              Your wallet address has been requested for a distribution.
            </p>

            {error && <p className="mb-4 text-sm text-red-700">{error}</p>}
            <input
              type="text"
              className="w-96 rounded-md bg-zinc-900 pr-12 text-gray-100 sm:text-sm"
              onChange={(e) => setWalletAddress(e.target.value)}
              disabled={isLoading}
              placeholder="e.g. 0xAbcdEfghIjKLMNOPqrst..."
              value={walletAddress}
            />
            <br />
            <button
              className="mt-4 cursor-pointer rounded-md bg-gradient-to-br from-indigo-800 to-orange-600 px-8 py-2 text-sm text-zinc-200 hover:from-indigo-900 hover:to-orange-800"
              onClick={acceptInvite}
              disabled={isLoading || isInvalidInvite}
              data-testid="update-invite"
            >
              {isLoading ? <LoadingSpinner /> : 'Submit'}
            </button>

            <p className="mt-12 text-xs text-zinc-600">
              Don{"'"}t have a wallet? We recommend using a{' '}
              {process.env.NEXT_PUBLIC_CHAIN === CHAIN_TYPE.SOLANA && (
                <a href="https://phantom.app/" className="text-blue-600 hover:text-blue-800">
                  {' '}
                  Phantom
                </a>
              )}
              {process.env.NEXT_PUBLIC_CHAIN === CHAIN_TYPE.ETHEREUM && (
                <a href="https://metamask.io/" className="text-blue-600 hover:text-blue-800">
                  {' '}
                  MetaMask
                </a>
              )}{' '}
              wallet.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-bold text-zinc-300">
              <span className="bg-gradient-to-br from-purple-800 to-red-500 bg-clip-text text-transparent">
                Thank you.
              </span>
            </h1>
            <p className="text-regular mt-2 text-zinc-400">
              Your wallet address has been securely stored in our system. You can close this window now.
            </p>
          </>
        )}
      </div>
    </BaseLayout>
  );
}
